import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2 } from "lucide-react";
import { PROJECT_TYPES } from "@/lib/projectTypes";

/**
 * The kind of document is asked first, because it decides the starting outline:
 * chapters for a thesis, IMRaD for an article, work packages for a proposal.
 */
export default function NewThesisModal({ open, onOpenChange, onCreate, onImportInstead }) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [type, setType] = useState("thesis");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setSubtitle("");
    setType("thesis");
    setLoading(false);
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await onCreate?.({
 title: title.trim() || "Nuovo documento",
 subtitle: subtitle.trim(),
        project_type: type,
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent data-testid="new-thesis-modal" className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl">Nuovo progetto</DialogTitle>
          <DialogDescription className="numa-meta">
            Il tipo di documento determina la struttura iniziale e il registro della revisione.
          </DialogDescription>
        </DialogHeader>

        {/* Someone who already has a draft on disk is not starting from
            nothing, and making them create an empty project first and paste
            into it is the wrong first step. */}
        {onImportInstead && (
          <button
            type="button"
            data-testid="import-instead"
            onClick={() => { onOpenChange(false); onImportInstead(); }}
            className="flex w-full items-center gap-3 rounded-md border border-line-strong bg-surface-2 px-3.5 py-3 text-left transition-colors hover:border-brand hover:bg-brand-soft/40"
          >
            <FileUp className="h-4 w-4 shrink-0 text-assistant" />
            <span className="min-w-0">
              <span className="block text-[13.5px] font-medium text-ink">
                Ho già un documento: importalo
              </span>
              <span className="numa-meta mt-0.5 block leading-snug">
                Da PDF o DOCX. NUMA ne riconosce capitoli e paragrafi e te li fa correggere
                prima di creare il progetto.
              </span>
            </span>
          </button>
        )}

        <form onSubmit={submit} className="space-y-5">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">Tipo di documento</legend>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {PROJECT_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  aria-pressed={type === t.id}
                  data-testid={`project-type-${t.id}`}
                  className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                    type === t.id
                      ? "border-brand bg-brand-soft"
                      : "border-line-strong hover:bg-surface-2"
                  }`}
                >
                  <span className={`block text-[13.5px] font-medium ${type === t.id ? "text-brand" : "text-ink"}`}>
                    {t.label}
                  </span>
                  <span className="mt-0.5 block text-2xs leading-snug text-ink-muted">{t.description}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="project-title">Titolo</Label>
            <Input
              id="project-title"
              data-testid="new-thesis-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="es. Comunicazione digitale e trasformazione del giornalismo"
              className="h-10"
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="project-subtitle">
              Sottotitolo <span className="font-normal text-ink-subtle">(opzionale)</span>
            </Label>
            <Input
              id="project-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="es. Studio su un campione di redazioni italiane"
              className="h-10"
              disabled={loading}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Annulla
            </Button>
            <Button type="submit" data-testid="confirm-new-thesis" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Crea progetto
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
